<template>
  <div class="userCamSelect">
    <setting-header />
    <header>
      <custom-check v-model="allSelected" @input="handleSelectAll"
        >一括選択
      </custom-check>
      <section v-if="!isPRD" class="userCamSelect_header--right">
        <h3>カメラ操作の許可</h3>
        <h5>OFFはLIVE映像のみ</h5>
      </section>
    </header>
    <v-divider class="userCamSelect_divider" />
    <section v-if="items.length === 0 || loading" class="userCamSelect_loading">
      <v-progress-circular
        :size="30"
        :width="2"
        indeterminate
      ></v-progress-circular>
    </section>
    <main>
      <section class="userCamSelect_left">
        <template v-for="(item, index) in mapRows(items)">
          <custom-check
            :key="`check-${index}`"
            v-model="selectedCams[item.id]"
            @input="handleSelectCam($event, item.id)"
          >
            {{ item.title }}
            <template v-if="!isPRD" #append>
              <span class="userCamSelect_switch-wrapper">
                <v-switch
                  v-model="switchCams[item.id]"
                  :disabled="!selectedCams[item.id]"
                  :ripple="false"
                  dense
                  @click.stop
                  @change="handleSwitch($event, item.id)"
                ></v-switch>
              </span>
            </template>
          </custom-check>
        </template>
      </section>
      <section
        class="userCamSelect_bottom"
        style="
          height: 30px;
          margin-top: 1px;
          padding-top: 15px;
          border-top: 1px solid #ccc;
        "
      >
        <v-btn
          class="editCamsGroup_actions--save"
          :disabled="!isChanged"
          :loading="loading"
          @click="handleSave"
        >
          保存
        </v-btn>
      </section>
    </main>
  </div>
</template>

<script>
import CustomCheck from '@/components/inputs/CustomCheck.vue';
import SettingHeader from '../SettingHeader.vue';
import { mapActions, mapGetters, mapState } from 'vuex';
import isEqual from 'fast-deep-equal';
import { AllCamerasIdGroup } from '@/globals/constants';

export default {
  name: 'UserCamSelect',
  components: {
    SettingHeader,
    CustomCheck,
  },
  data() {
    return {
      selectedCams: {},
      switchCams: {},
      displayCam: null,
      allSelected: false,
      isChanged: false,
      loading: false,
      originalItems: [],
    };
  },
  computed: {
    ...mapGetters('common', {
      isPRD: 'isPRD',
    }),
    ...mapState({
      newUserCamList: (state) => state.settings.newUserCamList,
      items: (state) => state.settings.groupCams,
    }),
    id() {
      return this.$route.params.userId;
    },
    isEdit() {
      return this.$route.name === 'settings-users-edit-select-cam';
    },
  },
  watch: {
    selectedCams: {
      handler: function () {
        this.changeNewUserCamList();
      },
      deep: true,
    },
    switchCams: {
      handler: function () {
        this.changeNewUserCamList();
      },
      deep: true,
    },
  },
  mounted() {
    this.newUserCamList.forEach((cam) => {
      this.$set(this.selectedCams, cam.id, true);
      this.$set(this.switchCams, cam.id, cam.livePermissionSwitch);
    });

    this.$nextTick(() => {
      let checkAllSwitches = false;
      this.originalItems = Object.keys(this.selectedCams)
        .filter((key) => this.selectedCams[key])
        .map((key) => {
          const camGroup = this.items.find(
            (cam) => String(cam.id) === String(key)
          );
          // Turn on カメラ操作の許可 by default if checked
          if (!this.switchCams[key]) {
            checkAllSwitches = true;
          }
          return {
            ...camGroup,
            livePermissionSwitch: !this.isPRD ? !!this.switchCams[key] : true,
          };
        });
      this.isChanged = checkAllSwitches;
      // check select-all check-box if all items are checked
      this.allSelected =
        Object.values(this.selectedCams).filter((selected) => selected)
          .length === this.items.length;
    });
    this.init();
  },
  methods: {
    ...mapActions('settings', [
      'setNewUserCamList',
      'fetchCameraGroup',
      'updateCameraGroupRole',
    ]),
    async init() {
      if (this.items.length < 1) {
        await this.fetchCameraGroup({ shareScope: 1 });
      }
    },
    mapRows(items) {
      return items.filter((item) => +item.id !== AllCamerasIdGroup);
    },
    changeNewUserCamList(onSave = false) {
      const newSelectedCams = Object.keys(this.selectedCams)
        .filter((key) => this.selectedCams[key])
        .map((key) => {
          const camGroup = this.items.find(
            (cam) => String(cam.id) === String(key)
          );
          return {
            ...camGroup,
            livePermissionSwitch: !!this.switchCams[key],
          };
        });

      if (onSave) {
        this.originalItems = newSelectedCams;
      }

      this.setNewUserCamList(newSelectedCams);

      this.isChanged = !isEqual(this.originalItems, newSelectedCams);
    },
    handleSelectCam(selected, id) {
      this.setCamSwitch(id, selected);
      this.allSelected =
        Object.values(this.selectedCams).filter((selected) => selected)
          .length === this.items.length;
    },
    setCamSwitch(id, selected) {
      this.$set(this.switchCams, id, selected && !this.isPRD);
    },
    handleSwitch() {},
    handleSelectAll(selected) {
      this.items.forEach((cam) => {
        if (selected) {
          // selecte all
          if (!this.selectedCams[cam.id]) {
            this.setCamSwitch(cam.id, true);
            this.$set(this.selectedCams, cam.id, true);
          }
        } else {
          // deselect all
          this.$set(this.selectedCams, cam.id, false);
          this.setCamSwitch(cam.id, false);
        }
      });
    },
    async handleSave() {
      this.loading = true;
      const cameraGroupRole = {
        avatarId: +this.id,
        policies: [],
      };
      this.newUserCamList.forEach((camGroup) => {
        if (this.isPRD) {
          // Turn on カメラ操作の許可 by default if checked
          camGroup.livePermissionSwitch = true;
        }
        cameraGroupRole.policies.push({
          cameraGroupId: camGroup.id,
          policyItems: camGroup.livePermissionSwitch
            ? [
                { name: 'CameraPolicy_Access', value: 1 },
                { name: 'CameraPolicy_LiveOnly', value: 0 },
              ]
            : [
                { name: 'CameraPolicy_Access', value: 0 },
                { name: 'CameraPolicy_LiveOnly', value: 1 },
              ],
        });
      });
      try {
        await this.updateCameraGroupRole(cameraGroupRole);
        await this.init();
        this.changeNewUserCamList(true);
        this.routerPush({
          name: 'settings-users-edit',
          params: { userId: this.id },
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss">
.userCamSelect {
  position: relative;
  &_header {
    &--right {
      min-width: fit-content;
      h3 {
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.67;
        letter-spacing: normal;
        text-align: center;
        color: #4b4b4b;
      }
      h5 {
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        line-height: 1.67;
        letter-spacing: normal;
        text-align: center;
        color: #989898;
      }
    }
  }
  &_switch-wrapper {
    width: 105px;
    text-align: center;
  }
  &_loading {
    top: 0;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    position: absolute;
    background-color: #ffffffab;
    z-index: 1;
  }
  &_edit {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;
    &.v-btn.v-size--default {
      font-size: 15px;
    }
  }
  &_flex-gap {
    display: flex;
    flex: 1;
  }
  &_divider {
    &.theme--light.v-divider {
      border-color: #ccc;
    }
  }

  &_live {
    width: 34px;
    height: 17px;
    padding: 0 4px;
    border-radius: 3px;
    background-color: #79c06e;
    font-size: 12px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.3;
    letter-spacing: 0.13px;
    text-align: left;
    color: #fff;
    position: absolute;
    top: 10px;
    left: 10px;
  }
  header {
    margin-top: 25px;
    margin-bottom: 10px;
    display: flex;
    align-items: flex-end;

    h5 {
      font-size: 12px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #989898;
    }
    .customCheck {
      margin-top: 20px;
      margin-left: 10px;
      .customCheck_label {
        font-size: 12px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        letter-spacing: normal;
        text-align: left;
        color: #4b4b4b;
        width: 400px;
      }
    }
  }
  main {
    display: flex;
    margin-bottom: 10px;
    flex-direction: column;
  }
  &_actions {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
  }
  &_submit {
    &.theme--light.v-btn.v-btn--has-bg {
      height: 36px;
      border-radius: 5px;
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16);
      background-color: #0075c2;
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.14;
      letter-spacing: normal;
      text-align: left;
      color: #fff;
    }
    &.theme--light.v-btn.v-btn--disabled.v-btn--has-bg {
      background-color: #f3f3f3 !important;
      box-shadow: none;
      color: #ccc !important;
    }
  }
  &_left {
    width: 100%;
    min-width: 560px;
    max-height: calc(100vh - 345px);
    overflow: auto;
    @include scrollBarCustom;
    .customCheck {
      padding: 12px 10px;
      &:not(:last-child) {
        border-bottom: 1px solid #eee;
      }
      .customCheck_label {
        width: 400px;
      }
    }
  }
  &_righ {
    margin-top: 11px;
    padding: 0 10px;
    .userCamSelect_cam {
      position: relative;
    }
    h5 {
      margin-top: 5px;
      font-size: 14px;
      font-weight: bold;
      font-stretch: normal;
      font-style: normal;
      letter-spacing: normal;
      text-align: left;
      color: #4b4b4b;
    }
  }
  .v-input.v-input--switch {
    margin-top: 0;
    padding-top: 0;
    display: inline-block;
    &.v-input--is-disabled {
      opacity: 0.4;
    }
    .v-input__control {
      .v-messages {
        display: none;
      }

      .v-input__slot {
        margin-bottom: 0;

        .v-input--selection-controls__input {
          .v-input--switch__track {
            border-radius: 15px;
            width: 42px;
            height: 22px;
            left: -1px;
            position: absolute;
            opacity: 1;
            right: 2px;
            top: calc(50% - 11px);
            color: #cccccc !important;
          }

          .v-input--switch__track.primary--text {
            color: #0075c2 !important;
          }

          .v-input--switch__thumb {
            color: #ffffff !important;
            width: 20px;
            height: 20px;
            box-shadow: -3px 0 3px 0 rgba(0, 0, 0, 0.1);
          }
        }

        .theme--light.v-label {
          font-size: 12px;
          font-weight: 500;
          font-stretch: normal;
          font-style: normal;
          letter-spacing: 0.6px;
          color: #666;
        }
      }
    }
  }
}
</style>
